import React from 'react';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * DatePicker — wraps Shadcn Popover + Calendar. Value in / out is a 'YYYY-MM-DD' string.
 * Props: value (string 'YYYY-MM-DD' | ''), onChange(string), placeholder, className, testId, minDate, maxDate
 */
const DatePicker = ({ value, onChange, placeholder = 'Pick a date', className, testId, minDate, maxDate, ...rest }) => {
  const asDate = React.useMemo(() => {
    if (!value) return undefined;
    const d = parse(value, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : undefined;
  }, [value]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className={cn(
            'h-9 w-36 justify-start text-left font-normal',
            !value && 'text-text-tertiary',
            className,
          )}
          data-testid={testId}
          {...rest}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(asDate, 'MMM d, yyyy') : placeholder}
          {value && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date"
              className="ml-auto text-text-tertiary hover:text-accent-brand rounded p-0.5 inline-flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(''); } }}
            >
              <X size={12} />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={asDate}
          onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')}
          disabled={(date) => {
            if (minDate && date < new Date(minDate)) return true;
            if (maxDate && date > new Date(maxDate)) return true;
            return false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
